import { z } from 'zod';
import { largoReferencia } from '../tipos.ts';
import { decimalPositivo, uuid } from './comun.ts';

/** Métodos que la dueña precarga (lo recibido en sus cuentas): Zelle y Binance/USDT. */
export const METODOS_PRECARGA = ['zelle', 'usdt'] as const;
export type MetodoPrecarga = (typeof METODOS_PRECARGA)[number];

export const pagoEsperadoInput = z.object({
  metodo: z.enum(METODOS_PRECARGA).default('zelle'),
  /**
   * Cuenta igual que al cobrar: solo letras y dígitos (guiones y espacios no suman). El mínimo configurado
   * (`referencia_min_caracteres`) lo exige el servidor al cargar, con el mismo código `referencia_corta` del cobro.
   */
  referencia: z.string().trim().min(4, 'La referencia tiene al menos 4 caracteres').max(60).refine((v) => largoReferencia(v) >= 4, 'La referencia tiene al menos 4 letras o dígitos'),
  /** Quién lo envió, como sale en el banco. */
  nombre: z.string().trim().min(2, 'Indica el nombre de quien lo envió').max(120),
  /** Monto recibido en $. */
  monto: decimalPositivo,
  fecha: z.iso.date().optional(),
  /** Banco o cuenta que lo recibió, como la dueña la llama («ZELLE CITI MG», «BINANCE»…). */
  cuenta: z.string().trim().min(2, 'Indica el banco o la cuenta (al menos 2 letras)').max(60).optional(),
  nota: z.string().trim().max(200).optional(),
  /**
   * La referencia ya se cobró por OTRO monto y la dueña decidió conciliarla igual: queda usada con ese cobro y la
   * diferencia (banco menos cobro) anotada para el reporte (respuesta de la dueña del 26-10-2026). Sin esto, el pago no
   * se carga y el sistema avisa con el cobro y los dos montos.
   */
  conciliar_diferencia: z.boolean().optional(),
});
export type PagoEsperadoInput = z.infer<typeof pagoEsperadoInput>;
export type PagoEsperadoEntrada = z.input<typeof pagoEsperadoInput>;

/** Carga de uno o de varios a la vez (pegados desde el banco). */
export const pagosEsperadosCargarInput = z.object({ items: z.array(pagoEsperadoInput).min(1).max(200) });
export type PagosEsperadosCargarInput = z.infer<typeof pagosEsperadosCargarInput>;

export const pagoEsperadoAnularInput = z.object({ motivo: z.string().trim().min(5, 'Indica el motivo').max(200) });
export const pagosEsperadosFiltros = z.object({
  estado: z.enum(['pendiente', 'usado', 'anulado']).optional(),
  metodo: z.enum(METODOS_PRECARGA).optional(),
  q: z.string().trim().max(80).optional(),
  desde: z.iso.date().optional(),
  hasta: z.iso.date().optional(),
  /** Banco o cuenta receptora (texto exacto, sin distinguir mayúsculas). */
  cuenta: z.string().trim().max(60).optional(),
  /** Tienda y caja donde se USÓ (solo aplica a los ya usados). */
  sucursal_id: uuid.optional(),
  caja_id: uuid.optional(),
});
export type PagosEsperadosFiltros = z.infer<typeof pagosEsperadosFiltros>;
/** Lo que escribe el cajero para encontrar un pago recibido: parte del nombre de quien lo envió o de la referencia. */
/** Al menos 3 letras o dígitos: signos y espacios no cuentan (un texto de puros comodines no busca nada). */
export const pagoEsperadoSugerirQuery = z.object({
  q: z
    .string()
    .trim()
    .max(60)
    .refine((v) => (v.match(/[\p{L}\p{N}]/gu) ?? []).length >= 3, 'Escribe al menos 3 letras o dígitos'),
});
export const pagoEsperadoBuscarQuery = z.object({ metodo: z.enum(METODOS_PRECARGA), referencia: z.string().trim().min(1).max(60) });
export const paramLineaPago = z.object({ id: uuid });

export interface PagoEsperadoDTO {
  id: string;
  metodo: MetodoPrecarga;
  referencia: string;
  nombre: string;
  monto: string;
  fecha: string | null;
  /** Banco o cuenta que lo recibió. */
  cuenta: string | null;
  nota: string | null;
  estado: 'pendiente' | 'usado' | 'anulado';
  cargado_por_nombre: string;
  cargado_en: string;
  /** Cobro en que se usó. */
  usado_en: string | null;
  usado_por_nombre: string | null;
  documento_id: string | null;
  documento_numero: string | null;
  cliente_razon_social: string | null;
  /** Tienda y caja donde se usó. */
  sucursal_id: string | null;
  sucursal_nombre: string | null;
  caja_id: string | null;
  caja_nombre: string | null;
  motivo_anulacion: string | null;
  /** Conciliado con un cobro de otro monto: lo del banco menos lo cobrado (null si coincidió). */
  diferencia: string | null;
}

/** Totales del reporte privado de Zelle/Binance, con los mismos filtros de la lista (sin el tope de filas). */
export interface PagosEsperadosResumenDTO {
  cantidad: number;
  monto: string;
  por_estado: { estado: 'pendiente' | 'usado' | 'anulado'; cantidad: number; monto: string }[];
  por_cuenta: { cuenta: string; cantidad: number; monto: string }[];
  /** Pagos conciliados con un cobro de otro monto y la suma de sus diferencias (banco menos cobro). */
  diferencias: { cantidad: number; monto: string };
  /** Para armar los filtros: cuentas escritas hasta ahora y cajas donde se ha usado alguno. */
  cuentas: string[];
  cajas: { id: string; nombre: string; sucursal_id: string; sucursal_nombre: string }[];
}

/** Lo que ve el cajero al buscar por nombre: solo pagos SIN usar que coinciden, nunca la lista completa. */
export interface PagoEsperadoSugerenciaDTO {
  metodo: MetodoPrecarga;
  referencia: string;
  nombre: string;
  cuenta: string | null;
  monto: string;
  fecha: string | null;
}

/**
 * Resultado de cargar cada pago: cargado · conciliado (ya había un cobro con esa referencia y ese monto, o la dueña
 * decidió conciliarlo anotando la diferencia: quedó verificado) · duplicado (la referencia ya estaba cargada) ·
 * monto_distinto (hay un cobro con esa referencia por otro monto: NO se carga; `cobro` dice cuál, para decidir).
 */
export interface ResultadoCargaDTO {
  referencia: string;
  resultado: 'cargado' | 'conciliado' | 'duplicado' | 'monto_distinto';
  detalle: string | null;
  pago_esperado: PagoEsperadoDTO | null;
  /** El cobro que ya usó la referencia, cuando el monto no coincide. */
  cobro?: CobroConReferenciaDTO | null;
}

/** El cobro que ya usó una referencia: documento, tienda, cajero, cuándo y los dos montos. */
export interface CobroConReferenciaDTO {
  documento: string | null;
  sucursal_nombre: string;
  cajero_nombre: string;
  recibido_en: string;
  monto_cobro: string;
  monto_banco: string;
  /** Lo del banco menos lo cobrado. */
  diferencia: string;
}

/** Lo que ve el cajero al escribir la referencia. */
export interface PagoEsperadoBusquedaDTO {
  estado: 'no_cargado' | 'pendiente' | 'usado';
  /** Método con el que la dueña lo cargó: la referencia se busca en Zelle y en Binance, elija lo que elija el cajero. */
  metodo: MetodoPrecarga | null;
  nombre: string | null;
  /** Banco o cuenta que lo recibió, si la dueña lo anotó. */
  cuenta?: string | null;
  monto: string | null;
  /** Dónde se usó, si ya se usó. */
  usado_en_documento: string | null;
}

/** Cobro por Zelle/Binance cuya referencia no estaba precargada: queda por verificar. */
export interface CobroSinVerificarDTO {
  pago_linea_id: string;
  metodo: MetodoPrecarga;
  referencia: string;
  monto: string;
  recibido_en: string;
  cajero_nombre: string;
  sucursal_nombre: string;
  cliente_razon_social: string;
  documento_id: string | null;
  documento_numero: string | null;
}
